import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { AssistantReply } from './types';

import { apiRequest, type ApiError } from '@/lib/api-client';
import { useSession } from '@/lib/session';

/**
 * The browser only ever talks to OUR API: the model provider's key lives on the server
 * (`ANTHROPIC_API_KEY`), and every action the assistant takes is the API calling its own routes with this session.
 */
export function useAssistantStatusQuery(): UseQueryResult<{ readonly enabled: boolean }, ApiError> {
  const { accessToken, activeShopId } = useSession();
  return useQuery({
    queryKey: ['assistant-status', activeShopId],
    queryFn: () =>
      apiRequest<{ enabled: boolean }>('/merchant/assistant/status', {
        accessToken,
        shopId: activeShopId,
      }),
    enabled: accessToken !== null && activeShopId !== null,
    staleTime: 5 * 60_000,
  });
}

export interface SendMessageInput {
  readonly conversationId: string | null;
  readonly message: string;
  readonly locale: string;
}

export function useSendAssistantMessageMutation(): UseMutationResult<
  AssistantReply,
  ApiError,
  SendMessageInput
> {
  const { accessToken, activeShopId } = useSession();
  return useMutation({
    mutationFn: ({ conversationId, message, locale }: SendMessageInput) =>
      apiRequest<AssistantReply>('/merchant/assistant/messages', {
        method: 'POST',
        body: { message, locale, ...(conversationId !== null ? { conversationId } : {}) },
        accessToken,
        shopId: activeShopId,
      }),
  });
}

export interface DecisionInput {
  readonly conversationId: string;
  readonly approve: boolean;
  readonly locale: string;
}

export function useAssistantDecisionMutation(): UseMutationResult<
  AssistantReply,
  ApiError,
  DecisionInput
> {
  const { accessToken, activeShopId } = useSession();
  return useMutation({
    mutationFn: ({ conversationId, approve, locale }: DecisionInput) =>
      apiRequest<AssistantReply>(`/merchant/assistant/conversations/${conversationId}/decision`, {
        method: 'POST',
        body: { approve, locale },
        accessToken,
        shopId: activeShopId,
      }),
  });
}
